import { ACTIVITY_STATE } from "Constants/constants";
import { selectAgentReportByActivityId } from "Reducers/activity/agentReport.selectors";
import { selectProjectActivityById } from "Reducers/activity/activity.selectors";
import { loadProjectActivity } from "Reducers/activity/thunks/loadProjectActivity.thunk";
import { loadLogFromActivity, streamFinished } from "Reducers/log";
import {
  runEndedWithoutReport,
  runReported,
  runStarted,
  selectAgentRun
} from "Reducers/agentRuns";
import { startAppListening } from "Store/listenerMiddleware";

import { isLogAnalyzerAgentTask } from "./logAnalyzerAgent/config";
import {
  notifyLogAnalyzerCompleted,
  notifyLogAnalyzerFailed,
  notifyLogAnalyzerStarted,
  removeLogAnalyzerStartedToast
} from "./logAnalyzerAgent/logAnalyzerRun.notifications";
import { isTroubleshootingAgentTask } from "./troubleshootingAgent/config";
import { notifyTroubleshootingCompleted } from "./troubleshootingAgent/troubleshootingRun.notifications";

import type { RootState } from "Store/configureStore";
import type { AppStartListening } from "Store/listenerMiddleware";

/** The run is over and has nothing further to write. */
const TERMINAL_ACTIVITY_STATES: string[] = [
  ACTIVITY_STATE.CANCELLED,
  ACTIVITY_STATE.COMPLETE
];

/**
 * How long to watch a run, from its start, for its activity to arrive and say
 * it is over.
 *
 * Twenty minutes is the task container's own ceiling, so a run that has not
 * settled by then is waiting on something that is not coming. Giving up
 * untracks the run, or the surface that started it shows it as running until
 * the user reloads. Anything shorter would untrack a run that may still be
 * going, and invite a second $5-10 run.
 */
const RUN_SETTLE_TIMEOUT_MS = 20 * 60_000;

/**
 * How long a settled run's log gets to deliver its report before the run is
 * called reportless. The report is the agent's LAST stdout line, written just
 * before the container exits, so it can still be a poll or two behind the state
 * change — concluding "no report" the instant the activity settles would lose
 * the toast on a run that reported perfectly. A report arriving ends this wait
 * early, so only a genuinely reportless run pays the full 15 seconds.
 */
const REPORT_GRACE_MS = 15_000;

/**
 * Owns everything an agent run needs AFTER the POST succeeds, so no component
 * has to stay mounted for it:
 *
 *  1. streams the new activity's log, which is what makes the report appear in
 *     the open panel and what the completion toast is derived from;
 *  2. settles the run once its activity is over — untracking it so a run that
 *     never reported stops blocking a new one;
 *  3. fires the completion toast the moment that log parses to a report.
 *
 * The log analyzer also gets its started toast here (listener 0), since the
 * Logs page modal that starts it closes on success.
 *
 * The stream is the load-bearing part. `loadLogFromActivity` spawns a worker
 * owned by a thunk closure, not by a component, and nothing terminates it — so
 * starting it here means a run reports itself even if the user closes the panel,
 * closes the log modal, and navigates to another page.
 *
 * The ACTIVITY is what decides a run is finished, never the stream. A closed
 * stream is not proof: `stream.worker.js` posts the same `done: true` whether
 * the log sealed or it simply gave up on a quiet one, and `log[id].streamEnded`
 * is shared by every worker on that activity, so it can be set by a viewer's
 * stream while the run's own is still polling. Reading the activity's state
 * instead means no stream closing early can end a run early.
 *
 * Kept with the Run variants rather than with the `log` slice: only agent runs
 * need this policy. Only the toasts differ per agent.
 */
export const registerAgentRunListeners = (
  startListening: AppStartListening
) => {
  // 0. The log analyzer's started toast. The completed toast replaces it.
  startListening({
    actionCreator: runStarted,
    effect: (action, { dispatch, extra }) => {
      const run = action.payload;
      if (!isLogAnalyzerAgentTask(run.taskType)) return;

      notifyLogAnalyzerStarted({ run, dispatch, intl: extra.getIntl() });
    }
  });

  // 1. Resolve the run's activity, stream its log, then settle the run.
  startListening({
    actionCreator: runStarted,
    effect: async (action, { dispatch, getState, condition, extra }) => {
      const { activityId, organizationId, projectId } = action.payload;
      const params = { organizationId, projectId, activityId };

      const readActivity = (state: RootState) =>
        selectProjectActivityById(state, params);

      // One budget for both waits below, so a late activity does not extend
      // the run past the container's ceiling.
      const deadline = Date.now() + RUN_SETTLE_TIMEOUT_MS;
      const timeLeft = () => Math.max(0, deadline - Date.now());

      const isTracked = () => Boolean(selectAgentRun(getState(), activityId));

      /**
       * Stops watching a run whose outcome is unknown: its activity never
       * arrived, or never settled, within the container's ceiling. Untracked silently, since nothing says it
       * failed. The log analyzer's started toast goes, or it keeps saying the
       * run is in progress.
       */
      const giveUp = () => {
        const run = selectAgentRun(getState(), activityId);
        if (!run) return;
        if (isLogAnalyzerAgentTask(run.taskType)) {
          removeLogAnalyzerStartedToast({ run, dispatch });
        }
        dispatch(runEndedWithoutReport(activityId));
      };

      if (!readActivity(getState())) {
        // Not in the project activity store yet — ask for it directly rather
        // than waiting on a poll tick, then wait for whichever source wins.
        dispatch(
          loadProjectActivity({
            organizationDescriptionId: organizationId,
            projectDescriptionId: projectId,
            activityId
          })
        );

        // The run thunk awaits a task refetch, so the activity is usually
        // already here. When that refetch failed, the project websocket has
        // to deliver it, however slow it is.
        const arrived = await condition(
          (_, currentState) =>
            Boolean(readActivity(currentState)) || !isTracked(),
          timeLeft()
        );
        if (!isTracked()) return;
        if (!arrived) {
          giveUp();
          return;
        }
      }

      const activity = readActivity(getState());
      if (!activity) return;

      // `loadLogFromActivity` reads `activity._links.log.href`, which is why the
      // whole object is needed and an id alone will not do. It keeps the worker
      // open for the whole run on its own, off the activity's task type.
      dispatch(loadLogFromActivity({ activity }));

      const hasReport = () =>
        Boolean(selectAgentReportByActivityId(getState(), activityId));

      const hasSettled = (state: RootState) => {
        const current = selectProjectActivityById(state, params);
        return Boolean(
          current && TERMINAL_ACTIVITY_STATES.includes(current.state)
        );
      };

      // One wait per run, started here rather than off the stream, so a run
      // leaves exactly one pending effect behind however many times its log is
      // streamed. It also ends the moment the run is settled elsewhere — a run
      // that reports untracks itself in listener 2 — rather than re-evaluating
      // on every action for twenty minutes with nothing left to decide.
      if (!hasSettled(getState())) {
        const settled = await condition(
          (_, currentState) => hasSettled(currentState) || !isTracked(),
          timeLeft()
        );
        if (!isTracked()) return;
        if (!settled) {
          giveUp();
          return;
        }
      }

      /**
       * The report may still be in flight, so give it a fixed grace; a report
       * landing ends this wait, since listener 2 untracks the run and toasts.
       *
       * Deliberately NOT gated on `log[activityId].streamEnded`. That flag is
       * per ACTIVITY, not per worker, and opening this run's log in the modal or
       * the activity list spawns a second worker over the same entry. Reading it
       * would let whichever worker finishes first declare the log drained while
       * the other is still polling, untracking a run that went on to report —
       * losing its toast and re-offering a paid run on a diagnosed failure.
       */
      if (!hasReport()) {
        await condition(() => hasReport() || !isTracked(), REPORT_GRACE_MS);
      }

      /**
       * The run is over and produced no report: it was cancelled, or the agent
       * died before printing one. Untrack it, or the surface that started it
       * keeps showing it as running ("Troubleshooting...", "Analyzing…") for
       * the rest of the session and the user cannot start another run after
       * cancelling one.
       *
       * A cancel is the user's own doing, so it gets no toast. The log
       * analyzer's started toast goes, or it keeps saying "started". A log
       * analyzer run that ended on its own gets the failed toast instead: the
       * Logs page has no panel to explain it. The troubleshooting panel
       * explains it in place with `AgentReport`.
       */
      const run = selectAgentRun(getState(), activityId);
      if (!hasReport() && run) {
        if (isLogAnalyzerAgentTask(run.taskType)) {
          const wasCancelled =
            readActivity(getState())?.state === ACTIVITY_STATE.CANCELLED;
          if (wasCancelled) {
            removeLogAnalyzerStartedToast({ run, dispatch });
          } else {
            notifyLogAnalyzerFailed({ run, dispatch, intl: extra.getIntl() });
          }
        }
        dispatch(runEndedWithoutReport(activityId));
      }
    }
  });

  // 2. Completion toast, off the log stream.
  startListening({
    actionCreator: streamFinished,
    effect: (action, { dispatch, getState, extra }) => {
      const { activityId } = action.payload;

      // Every log batch of every activity lands here, so bail on the cheap
      // lookup before parsing anything.
      const run = selectAgentRun(getState(), activityId);
      if (!run) return;

      // The report is the agent's last stdout line, so it can land before the
      // stream closes. Parse each batch and fire on the first one that carries
      // it; untracking makes this at-most-once per run. A batch without one says
      // nothing — whether the run is finished is listener 1's question.
      if (!selectAgentReportByActivityId(getState(), activityId)) return;

      dispatch(runReported(activityId));

      const notify = { run, dispatch, intl: extra.getIntl() };
      if (isTroubleshootingAgentTask(run.taskType)) {
        notifyTroubleshootingCompleted(notify);
      } else if (isLogAnalyzerAgentTask(run.taskType)) {
        notifyLogAnalyzerCompleted(notify);
      }
    }
  });
};

// Register against the production middleware singleton.
registerAgentRunListeners(startAppListening);
